import { useState } from "react";
import Logo from "../components/Logo";
import { useTheme } from "../hooks/useAppShell";
import { Sun, Moon, CheckCircle2, Music4 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicDemoSubmit() {
  const { theme, toggle: toggleTheme } = useTheme();
  const [form, setForm] = useState({
    producer_name: "",
    producer_email: "",
    song_title: "",
    genre: "",
    audio_link: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/demo-submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Submit failed (${res.status})`);
      setSuccess(true);
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grain" style={{ minHeight: "100vh", padding: "40px 20px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "-15%", left: "-8%", width: 600, height: 600, background: "radial-gradient(circle, rgba(124,108,255,0.24), transparent 70%)", filter: "blur(80px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-15%", right: "-8%", width: 600, height: 600, background: "radial-gradient(circle, rgba(76,232,206,0.16), transparent 70%)", filter: "blur(80px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 640, margin: "0 auto", position: "relative", zIndex: 2 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Logo size={64} />
            <div>
              <div className="muted" style={{ fontSize: 10, letterSpacing: "0.22em", fontWeight: 600 }}>YE MUSIC</div>
              <div className="muted" style={{ fontSize: 9, letterSpacing: "0.18em", opacity: 0.7 }}>DEMO SUBMISSIONS</div>
            </div>
          </div>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-public"
            aria-label="Toggle theme"
            className="btn"
            style={{ padding: "8px 12px", borderRadius: 999 }}
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>

        {success ? (
          <div className="panel" data-testid="demo-submit-success" style={{ padding: 44, textAlign: "center" }}>
            <CheckCircle2 size={56} style={{ color: "var(--mint)", margin: "0 auto 18px", display: "block" }} />
            <h1 style={{ fontSize: 26, marginBottom: 10 }}>Thanks — we got it 🎶</h1>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.65, maxWidth: 440, margin: "0 auto 24px" }}>
              Your demo is now in our review queue. If it feels like a fit, we'll reach out to the email you provided within a few weeks. Please don't submit the same track twice — we hear every one.
            </p>
            <button
              onClick={() => { setSuccess(false); setForm({ producer_name: "", producer_email: "", song_title: "", genre: "", audio_link: "", notes: "" }); }}
              data-testid="submit-another-btn"
              className="btn"
              style={{ padding: "10px 20px", borderRadius: 12 }}
            >
              Submit another demo
            </button>
          </div>
        ) : (
          <div className="panel" style={{ padding: 36 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 999, background: "rgba(124,108,255,0.10)", border: "1px solid rgba(124,108,255,0.28)", marginBottom: 14, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600, color: "#c4b8ff" }}>
              <Music4 size={12} /> Submit a demo
            </div>
            <h1 style={{ fontSize: 30, marginBottom: 10, lineHeight: 1.1 }}>Send us your track</h1>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.65, marginBottom: 28 }}>
              We listen to every demo. Upload your track to SoundCloud, Google Drive, or Dropbox and drop the link below — we'll get back to you if it feels right.
            </p>

            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <PublicField label="Your name *">
                <input className="input" required value={form.producer_name} onChange={(e) => set("producer_name", e.target.value)} data-testid="field-producer-name" placeholder="How should we address you?" />
              </PublicField>
              <PublicField label="Email * — we'll only use this to respond">
                <input className="input" type="email" required value={form.producer_email} onChange={(e) => set("producer_email", e.target.value)} data-testid="field-producer-email" placeholder="you@example.com" />
              </PublicField>
              <PublicField label="Song title *">
                <input className="input" required value={form.song_title} onChange={(e) => set("song_title", e.target.value)} data-testid="field-song-title" placeholder="Name your track" />
              </PublicField>
              <PublicField label="Genre">
                <input className="input" value={form.genre} onChange={(e) => set("genre", e.target.value)} data-testid="field-genre" placeholder="Hip-Hop, Indie, Electronic…" />
              </PublicField>
              <PublicField label="Audio link * — public link to your song">
                <input className="input" type="url" required value={form.audio_link} onChange={(e) => set("audio_link", e.target.value)} data-testid="field-audio-link" placeholder="https://soundcloud.com/… or Google Drive link" />
              </PublicField>
              <PublicField label="Anything you want us to know (optional)">
                <textarea className="input" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="field-notes" placeholder="Tell us about the track, your journey, or what you're looking for from a label" />
              </PublicField>

              {err && (
                <div data-testid="submit-error" style={{ color: "var(--danger)", fontSize: 12.5, padding: "10px 14px", background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.32)", borderRadius: 10 }}>
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-demo-btn"
                className="btn btn-primary"
                style={{ padding: "14px 20px", fontSize: 14, borderRadius: 12, justifyContent: "center", opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? "Submitting…" : "Send my demo"}
              </button>
              <p className="muted" style={{ fontSize: 11, textAlign: "center", lineHeight: 1.6 }}>
                By submitting, you confirm you own or have rights to the material. YE Music will never share your submission publicly.
              </p>
            </form>
          </div>
        )}

        <div className="muted" style={{ textAlign: "center", marginTop: 24, fontSize: 11, letterSpacing: "0.14em" }}>
          © YE MUSIC · BUILT FOR ARTISTS. BACKED BY VISION.
        </div>
      </div>
    </div>
  );
}

function PublicField({ label, children }) {
  return (
    <label style={{ fontSize: 12 }}>
      <span className="muted" style={{ display: "block", marginBottom: 6, fontSize: 11, letterSpacing: "0.04em", fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}
